import type { AttributionAnalysis, SharedProps } from '../../types';
import React, { useCallback, useMemo } from 'react';
import type { AttributionSubject, AttributionView, TrackAnalysisProps } from 'venn-components';
import { PrintContainerDimensions } from 'venn-components';
import { type AnalysisSubject, logMessageToSentry, useHasFF } from 'venn-utils';
import type { PerformanceAttribution, PortfolioPerformanceAttributions } from 'venn-api';
import AttributionBlock from './AttibutionBlock';
import sortBy from 'lodash/sortBy';
import type { AttributionRow } from './types';
import { compact, uniq } from 'lodash';

const hasStrategies = (contribution: PortfolioPerformanceAttributions | undefined, relative: boolean) => {
  if (!contribution) {
    return false;
  }
  const returns = relative ? contribution.periodExcessReturn : contribution.periodReturn;
  if (returns.funds.length !== returns.strategies.length) {
    return true;
  }

  const sortedFunds = sortBy(returns.funds, 'name');
  const sortedStrategies = sortBy(returns.strategies, 'name');

  for (let i = 0; i < sortedFunds.length; i++) {
    if (sortedFunds[i]!.name !== sortedStrategies[i]!.name) {
      return true;
    }
  }

  return false;
};

const createRows =
  (attributions: AttributionAnalysis, relative: boolean, subject: AnalysisSubject) =>
  (view: AttributionView, selectedSubject: AttributionSubject): AttributionRow[] => {
    const historicalContributions = attributions.historicalContribution?.[selectedSubject];
    const forecastContributions = attributions.forecastContribution?.[selectedSubject];

    if (!historicalContributions && !forecastContributions) {
      return [];
    }

    if (view === 'strategies' && !hasStrategies(historicalContributions, relative)) {
      return [];
    }

    const getReturns = (contributions?: PortfolioPerformanceAttributions): PerformanceAttribution[] | undefined =>
      relative ? contributions?.periodExcessReturn?.[view] : contributions?.periodReturn?.[view];
    const getVolatility = (contributions?: PortfolioPerformanceAttributions): PerformanceAttribution[] | undefined =>
      relative ? contributions?.trackingError?.[view] : contributions?.volatility?.[view];
    const getSharpe = (contributions?: PortfolioPerformanceAttributions): PerformanceAttribution[] | undefined =>
      relative ? contributions?.informationRatio?.[view] : contributions?.sharpe?.[view];

    const returns = getReturns(historicalContributions);
    const forecastReturns = getReturns(forecastContributions);
    const volatility = getVolatility(historicalContributions);
    const forecastVolatility = getVolatility(forecastContributions);
    const sharpe = getSharpe(historicalContributions);
    const forecastSharpe = getSharpe(forecastContributions);
    const rowNames = compact(
      uniq([
        ...(returns?.map((r) => r.name) ?? []),
        ...(forecastReturns?.map((r) => r.name) ?? []),
        ...(volatility?.map((r) => r.name) ?? []),
        ...(forecastVolatility?.map((r) => r.name) ?? []),
        ...(sharpe?.map((r) => r.name) ?? []),
        ...(forecastSharpe?.map((r) => r.name) ?? []),
      ]),
    );

    const rows = rowNames?.map(
      (name) =>
        ({
          name,
          optionData: returns?.find((r) => r.name === name)?.optionData,
          weight: returns?.find((r) => r.name === name)?.weight,
          return: returns?.find((r) => r.name === name)?.value,
          returnContribution: returns?.find((r) => r.name === name)?.contributionValue,
          returnForecastContribution: forecastReturns?.find((r) => r.name === name)?.contributionValue,
          volatility: volatility?.find((r) => r.name === name)?.value,
          volatilityContribution: volatility?.find((r) => r.name === name)?.contributionValue,
          volatilityForecastContribution: forecastVolatility?.find((r) => r.name === name)?.contributionValue,
          sharpe: sharpe?.find((r) => r.name === name)?.value,
          sharpeContribution: sharpe?.find((r) => r.name === name)?.contributionValue,
          sharpeForecastContribution: forecastSharpe?.find((r) => r.name === name)?.contributionValue,
          start: historicalContributions?.analysisStart,
          end: historicalContributions?.analysisEnd,
        }) as AttributionRow,
    );

    if (!rows?.length) {
      return [];
    }

    if (subject.type === 'investment') {
      return rows.filter((r) => r.name === subject.name);
    }

    return rows;
  };

export interface AttributionProps extends SharedProps {
  trackingProps: TrackAnalysisProps;
}

const Attribution = ({ analyses, analysisConfig, trackingProps, downloadMetaData }: AttributionProps) => {
  const { subject, relative } = analysisConfig;
  const hasHistoricalPortfolioContribution = useHasFF('historicals_portfolio_contribution_ff');

  const attributions = analyses?.results?.attributions;

  const cumulativeReturn = !attributions?.historicalContribution?.subject?.periodAnnualized;

  const getRows = useMemo(() => {
    if (!attributions || !subject) {
      return undefined;
    }
    return createRows(attributions, relative, subject);
  }, [attributions, relative, subject]);

  const getAnalysisTimeFrame = useCallback(
    // @ts-expect-error: fixme
    (selectedSubject) => {
      if (!attributions) {
        return {
          startTime: undefined,
          endTime: undefined,
        };
      }
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const selectedAnalysis = attributions.historicalContribution[selectedSubject];
      return {
        startTime: selectedAnalysis?.analysisStart,
        endTime: selectedAnalysis?.analysisEnd,
      };
    },
    [attributions],
  );

  if (!subject || !getRows || !attributions) {
    logMessageToSentry('Failed to render Attribution Block because of incorrect initialization.');
    return null;
  }

  const emptyMessage =
    !hasHistoricalPortfolioContribution && subject.isHistoricalPortfolio
      ? 'This block does not support portfolios with historical allocations.'
      : `Add this investment to the ${
          subject.isInvestmentInPortfolio ? 'current portfolio' : 'Master Portfolio'
        } to view Performance Attribution Analysis.`;

  return (
    <PrintContainerDimensions>
      {({ print }) => (
        <AttributionBlock
          subject={subject}
          getRows={getRows}
          relative={relative}
          trackingProps={trackingProps}
          showTooltip
          showTotal
          cumulativeReturn={cumulativeReturn}
          errorMessage={attributions.historicalContribution.message?.text}
          errorCode={attributions.historicalContribution.message?.code}
          emptyMessage={emptyMessage}
          hasComparison={!!attributions.historicalContribution.category}
          getAnalysisTimeFrame={getAnalysisTimeFrame}
          downloadMetaData={downloadMetaData}
          print={print}
        />
      )}
    </PrintContainerDimensions>
  );
};

export default Attribution;
